/* ==========================================================================
   msc-rail.js: shared rail controller (scrubber + arrow buttons) and ARIA tabs.
   Load once per section that needs it:
     <script src="{{ 'msc-rail.js' | asset_url }}" defer="defer"></script>
   It is safe to include from several sections; it only runs once.

   Rails
     [data-msc-rail-root]   Wrapper that scopes one rail with its controls.
     [data-msc-rail]        The scrolling element (usually .msc-rail). If the root
                            holds several (e.g. one per tab panel), the first one
                            that is not inside a [hidden] element is controlled.
     [data-msc-rail-prev]   Button that scrolls back one page.
     [data-msc-rail-next]   Button that scrolls forward one page.
     [data-msc-rail-scrub]  The moving bar inside the track (.msc-scrub__bar).
                            Its width shows the visible share, its position the
                            scroll position.
     Root state:            data-msc-rail-static="true" is set on the root when
                            nothing overflows (style it to hide the controls).
     Events:                dispatch `msc:rail-refresh` on the root after changing
                            which rail is visible; it re-measures.

   Tabs (WAI-ARIA tabs pattern, automatic activation)
     [data-msc-tabs]        On the element with role="tablist". Children with
                            role="tab" and aria-controls="<panel id>". Panels need
                            role="tabpanel"; inactive panels get [hidden].
                            Arrow keys, Home and End move between tabs. After a
                            switch, the closest [data-msc-rail-root] is refreshed.

   Theme editor: re-initialises on shopify:section:load, and
   shopify:block:select on a tab or on a block inside a panel selects that tab.
   ========================================================================== */
(function () {
  if (window.MscRail) return;

  function visibleRail(root) {
    var rails = root.querySelectorAll('[data-msc-rail]');
    for (var i = 0; i < rails.length; i++) {
      if (!rails[i].closest('[hidden]')) return rails[i];
    }
    return null;
  }

  function Rail(root) {
    this.root = root;
    this.prev = root.querySelector('[data-msc-rail-prev]');
    this.next = root.querySelector('[data-msc-rail-next]');
    this.bar = root.querySelector('[data-msc-rail-scrub]');
    this.onScroll = this.update.bind(this);
    this.raf = 0;
    var self = this;
    if (this.prev) this.prev.addEventListener('click', function () { self.page(-1); });
    if (this.next) this.next.addEventListener('click', function () { self.page(1); });
    root.addEventListener('msc:rail-refresh', function () { self.bind(); });
    if (window.ResizeObserver) {
      this.ro = new ResizeObserver(function () { self.schedule(); });
      this.ro.observe(root);
    } else {
      window.addEventListener('resize', function () { self.schedule(); });
    }
    this.bind();
  }

  Rail.prototype.bind = function () {
    var rail = visibleRail(this.root);
    if (this.rail && this.rail !== rail) this.rail.removeEventListener('scroll', this.onScroll);
    if (rail && this.rail !== rail) rail.addEventListener('scroll', this.onScroll, { passive: true });
    this.rail = rail;
    this.update();
  };

  Rail.prototype.schedule = function () {
    var self = this;
    cancelAnimationFrame(this.raf);
    this.raf = requestAnimationFrame(function () { self.update(); });
  };

  Rail.prototype.step = function () {
    var rail = this.rail;
    var first = rail.firstElementChild;
    if (!first) return rail.clientWidth;
    var gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
    var item = first.getBoundingClientRect().width + gap;
    var per = Math.max(1, Math.floor((rail.clientWidth + gap) / item));
    return per * item;
  };

  Rail.prototype.page = function (dir) {
    if (!this.rail) return;
    var reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.rail.scrollBy({ left: dir * this.step(), behavior: reduce ? 'auto' : 'smooth' });
  };

  Rail.prototype.update = function () {
    var rail = this.rail;
    if (!rail) return;
    var max = rail.scrollWidth - rail.clientWidth;
    var stat = max <= 2;
    this.root.setAttribute('data-msc-rail-static', stat ? 'true' : 'false');
    var pos = stat ? 0 : Math.min(1, Math.max(0, rail.scrollLeft / max));
    if (this.prev) this.prev.disabled = stat || rail.scrollLeft <= 2;
    if (this.next) this.next.disabled = stat || rail.scrollLeft >= max - 2;
    if (this.bar) {
      var share = stat ? 1 : Math.max(0.12, rail.clientWidth / rail.scrollWidth);
      this.bar.style.width = (share * 100) + '%';
      this.bar.style.transform = 'translateX(' + (pos * (1 - share) / share * 100) + '%)';
    }
  };

  /* ---------- tabs ---------- */
  function select(tab, focus) {
    var list = tab.closest('[role="tablist"]');
    var tabs = list.querySelectorAll('[role="tab"]');
    for (var i = 0; i < tabs.length; i++) {
      var t = tabs[i];
      var on = t === tab;
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      t.tabIndex = on ? 0 : -1;
      var panel = document.getElementById(t.getAttribute('aria-controls'));
      if (panel) panel.hidden = !on;
    }
    if (focus) tab.focus();
    var root = list.closest('[data-msc-rail-root]') || (document.getElementById(tab.getAttribute('aria-controls')) || list).closest('[data-msc-rail-root]');
    if (root) root.dispatchEvent(new CustomEvent('msc:rail-refresh'));
  }

  function initTabs(list) {
    if (list.__mscTabs) return;
    list.__mscTabs = true;
    list.addEventListener('click', function (e) {
      var tab = e.target.closest('[role="tab"]');
      if (tab && list.contains(tab)) select(tab, false);
    });
    list.addEventListener('keydown', function (e) {
      var tabs = Array.prototype.slice.call(list.querySelectorAll('[role="tab"]'));
      var i = tabs.indexOf(document.activeElement);
      if (i < 0) return;
      var n = null;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') n = (i + 1) % tabs.length;
      else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') n = (i - 1 + tabs.length) % tabs.length;
      else if (e.key === 'Home') n = 0;
      else if (e.key === 'End') n = tabs.length - 1;
      if (n === null) return;
      e.preventDefault();
      select(tabs[n], true);
    });
  }

  function init(scope) {
    scope = scope || document;
    var roots = scope.querySelectorAll('[data-msc-rail-root]');
    for (var i = 0; i < roots.length; i++) {
      if (!roots[i].__mscRail) roots[i].__mscRail = new Rail(roots[i]);
      else roots[i].__mscRail.bind();
    }
    var lists = scope.querySelectorAll('[data-msc-tabs]');
    for (var j = 0; j < lists.length; j++) initTabs(lists[j]);
  }

  window.MscRail = { init: init, selectTab: select };

  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  document.addEventListener('shopify:block:select', function (e) {
    var el = e.target;
    var tab = el.matches && el.matches('[role="tab"]') ? el : null;
    if (!tab) {
      var panel = el.closest && el.closest('[role="tabpanel"]');
      if (panel && panel.id) tab = document.querySelector('[role="tab"][aria-controls="' + panel.id + '"]');
    }
    if (tab) select(tab, false);
  });

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
})();
